// Integrations
// -------------
// Two-section design:
//   1. Credentials card (admin only) — create, view, edit, delete saved tokens/API keys
//   2. Web apps card — view integration badges per app; admins can open a modal to manage
//
// Binding form uses a credential picker (filtered by integration type) instead of a raw
// token field. Inline validation prevents submitting empty or invalid forms.

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import { API, graphqlOperation as _graphqlOperation } from "aws-amplify";
import { assertNoTenantIdInVariables } from "@/services/graphql/assertNoTenantId";

import {
  Eye,
  EyeOff,
  FileText,
  Globe,
  Key,
  Lock,
  Plus,
  ShieldAlert,
  X,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { FormField } from "@/components/ui/form-field";
import SortSelect from "@/components/common/SortSelect";
import { getGraphData } from "@/utils/transform";
import { listIntegrations, listIntegrationCredentials } from "@/services/graphql/queries";
import {
  createIntegration,
  updateIntegration,
  deleteIntegration,
  toggleIntegration,
  testIntegrationConnection,
  createIntegrationCredential,
  updateIntegrationCredential,
  deleteIntegrationCredential,
} from "@/services/graphql/mutations";
import {
  INTEGRATION_ICON_MAP,
  INTEGRATION_TYPE_META,
} from "@/components/integrations/IntegrationIcons";
import ConfirmModal from "@/components/common/ConfirmModal";
import { CacheKey, EMPTY_STRING } from "@/constants/definitions";
import UserClass from "@/models/user";
import WebApplicationClass from "@/models/webApplication";
import { getCache, setCache } from "@/utils/cache";
import { deriveAppAbbrev, deriveAppColor } from "@/utils/appIdentity";
import { useAppViewScope } from "@/utils/useAppViewScope";

// Force Cognito User Pools auth on every GraphQL call.
const graphqlOperation = (query: any, variables?: any) => {
  // Reject any client-side tenantId leak before the request leaves
  // the wrapper. See src/services/graphql/assertNoTenantId.ts for
  // the contract — symmetric to the BE rule "never accept tenantId
  // from client input."
  assertNoTenantIdInVariables(variables);
  return {
    ..._graphqlOperation(query, variables),
    authMode: "AMAZON_COGNITO_USER_POOLS" as const,
  };
};

// Integration types that require a saved credential.
// Slack and Teams embed auth in the webhook URL (stored in connectionConfig).
// Splunk/Sentinel also require credentials but are not currently offered — see
// INTEGRATION_TYPE_META (only Slack/Jira/Teams are enabled) and the server-side
// ENABLED_INTEGRATION_TYPES allowlist.
const INTEGRATION_TYPES_REQUIRING_CREDENTIAL = new Set(["jira"]);

// Status dot colors
const STATUS_COLOR_SUCCESS  = "#4caf50";
const STATUS_COLOR_FAILED   = "#ff9800";
const STATUS_COLOR_DISABLED = "#f44336";
const STATUS_COLOR_PENDING  = "#9e9e9e";

const MAX_CONSECUTIVE_FAILURES = 5;

// Human-readable label for the credential field, by integration type.
// (Splunk "HEC Token" / Sentinel "Shared Key" labels return here when those
// types are re-enabled.)
const CREDENTIAL_TOKEN_LABEL: Record<string, string> = {
  jira: "API Token",
};

// ----------------------------------------
//   Interfaces
// ----------------------------------------

interface IntegrationCredentialItem {
  id: string;
  integrationType: string;
  displayName: string;
  credentialType: string;
  maskedSuffix?: string;
  credentialLastRotated?: number;
  credentialExpiresAt?: number;
  createdAt: number;
  updatedAt: number;
  createdBy?: string;
}

interface IntegrationItem {
  id: string;
  webAppId: string;
  integrationType: string;
  displayName: string;
  enabled: boolean;
  createdAt: number;
  updatedAt: number;
  createdBy: string;
  connectionConfig: string; // AWSJSON
  eventFilter: string;      // AWSJSON
  credentialId?: string;    // Only present for admin callers
  credential?: {
    credentialSet: boolean;
    credentialType: string;
    credentialLastRotated: number;
    credentialExpiresAt: number;
    maskedSuffix: string;
  };
  lastDeliveryAt: number;
  lastDeliveryStatus: string;
  lastErrorMessage: string;
  consecutiveFailures: number;
}

type ModalView = "list" | "add" | "edit";
type CredentialView = "none" | "create" | "edit";

// Discriminated union for the confirm-delete modal — null when no dialog is open.
// Carries the id of the thing to delete plus a display label for the message body.
type PendingConfirmation =
  | { kind: "credential"; id: string }
  | { kind: "integration"; id: string; displayName: string };

interface IntegrationsProps {
  user: UserClass;
  listOfWebapps: WebApplicationClass[];
  isAdmin: boolean;
}

// Credential form state grouped into a single object
interface CredentialFormState {
  type: string;
  displayName: string;
  token: string;
  showToken: boolean;
  errors: Record<string, string>;
}

// Binding form state grouped into a single object
interface BindingFormState {
  displayName: string;
  credentialId: string;
  baseUrl: string;
  projectKey: string;
  issueType: string;
  authMode: string;
  email: string;
  eventTypes: string[];
  minSeverity: string;
  format: string;
  workspaceId: string;
  errors: Record<string, string>;
}

const INITIAL_CREDENTIAL_FORM: CredentialFormState = {
  type: EMPTY_STRING,
  displayName: EMPTY_STRING,
  token: EMPTY_STRING,
  showToken: false,
  errors: {},
};

const INITIAL_BINDING_FORM: BindingFormState = {
  displayName: EMPTY_STRING,
  credentialId: EMPTY_STRING,
  baseUrl: EMPTY_STRING,
  projectKey: EMPTY_STRING,
  issueType: "Bug",
  authMode: "pat",
  email: EMPTY_STRING,
  eventTypes: ["*"],
  minSeverity: EMPTY_STRING,
  format: "json",
  workspaceId: EMPTY_STRING,
  errors: {},
};

// ----------------------------------------
//   Helper functions (pure, no state)
// ----------------------------------------

function groupIntegrationsByApp(items: IntegrationItem[]): Record<string, IntegrationItem[]> {
  const integrationsByApp: Record<string, IntegrationItem[]> = {};
  for (const item of items) {
    if (!integrationsByApp[item.webAppId]) {
      integrationsByApp[item.webAppId] = [];
    }
    integrationsByApp[item.webAppId].push(item);
  }
  return integrationsByApp;
}

function getIntegrationStatus(integration: IntegrationItem): { color: string; label: string } {
  if (!integration.enabled) {
    if (integration.consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
      return {
        color: STATUS_COLOR_DISABLED,
        label: `Disabled — ${integration.lastErrorMessage || "too many failures"}`,
      };
    }
    return { color: STATUS_COLOR_DISABLED, label: "Disabled" };
  }
  if (integration.credential?.credentialExpiresAt && integration.credential.credentialExpiresAt > 0) {
    if (integration.credential.credentialExpiresAt < Date.now() / 1000) {
      return { color: STATUS_COLOR_FAILED, label: "Credential expired" };
    }
  }
  if (integration.lastDeliveryStatus === "success") {
    return {
      color: STATUS_COLOR_SUCCESS,
      label: `Last delivered ${formatTimeAgo(integration.lastDeliveryAt)}`,
    };
  }
  if (integration.lastDeliveryStatus === "failed") {
    return {
      color: STATUS_COLOR_FAILED,
      label: integration.lastErrorMessage || "Last delivery failed",
    };
  }
  return { color: STATUS_COLOR_PENDING, label: "Waiting for first event" };
}

function formatTimeAgo(timestamp: number): string {
  if (!timestamp) return "never";
  const seconds = Math.floor(Date.now() / 1000 - timestamp);
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

function formatDate(timestamp: number): string {
  if (!timestamp) return "";
  return new Date(timestamp * 1000).toLocaleDateString();
}

// ----------------------------------------
//   Component
// ----------------------------------------

function Integrations({ user, listOfWebapps, isAdmin }: IntegrationsProps): React.ReactElement {
  // My Apps / All Apps toggle — admin/owner only, persisted per-user
  // in localStorage via useAppViewScope. Mirrors the affordance on
  // the Dashboard and Applications page so the three surfaces stay
  // consistent and the choice carries between them.
  const [displayMyApps, setDisplayMyApps] = useAppViewScope(user.emailaddress);

  // visibleWebapps drives the Applications list below. The full
  // listOfWebapps is still used for the "No applications found"
  // empty state so it reflects organization-level reality rather
  // than the current filter scope.
  const visibleWebapps = useMemo(() => {
    if (displayMyApps && isAdmin) {
      return listOfWebapps.filter((app) => app.owner === user.emailaddress);
    }
    return listOfWebapps;
  }, [listOfWebapps, displayMyApps, isAdmin, user.emailaddress]);

  // ---- Credentials (admin only) ----
  const [credentials, setCredentials] = useState<IntegrationCredentialItem[]>([]);
  const [credentialView, setCredentialView] = useState<CredentialView>("none");
  const [editingCredential, setEditingCredential] = useState<IntegrationCredentialItem | null>(null);
  const [credentialForm, setCredentialForm] = useState<CredentialFormState>(INITIAL_CREDENTIAL_FORM);

  // ---- Integration bindings ----
  const [integrationsByApp, setIntegrationsByApp] = useState<Record<string, IntegrationItem[]>>({});
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(EMPTY_STRING);

  // ---- Confirmation modal ----
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | null>(null);

  // ---- "No credential of this type" info modal ----
  const [missingCredentialType, setMissingCredentialType] = useState<string | null>(null);

  // ---- Modal (per-app management) ----
  const [managingWebApp, setManagingWebApp] = useState<WebApplicationClass | null>(null);
  const [modalView, setModalView] = useState<ModalView>("list");
  const [selectedType, setSelectedType] = useState(EMPTY_STRING);
  const [editingIntegration, setEditingIntegration] = useState<IntegrationItem | null>(null);
  const [successMessage, setSuccessMessage] = useState(EMPTY_STRING);
  const [testingConnection, setTestingConnection] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // ---- Binding form fields ----
  const [bindingForm, setBindingForm] = useState<BindingFormState>(INITIAL_BINDING_FORM);

  // ----------------------------------------
  //   Data Loading
  // ----------------------------------------

  const loadAllIntegrations = useCallback(async (): Promise<void> => {
    // 1. Apply cached value immediately if available (even if stale)
    try {
      const cached = await getCache<IntegrationItem[]>(CacheKey.listIntegrations);
      if (cached?.value) {
        setIntegrationsByApp(groupIntegrationsByApp(cached.value));
        setLoading(false);
      } else {
        setLoading(true);
      }
    } catch {
      setLoading(true);
    }

    // 2. Fetch fresh and refresh state + cache
    try {
      const response = await API.graphql(
        graphqlOperation(listIntegrations, { webAppId: null })
      );
      const responseData = getGraphData(response, "listIntegrations");
      const items: IntegrationItem[] = responseData?.items || [];

      setIntegrationsByApp(groupIntegrationsByApp(items));
      setLoading(false);
      setCache(CacheKey.listIntegrations, items);
    } catch (loadError) {
      console.error("Failed to load integrations:", loadError);
      setLoading(false);
      setErrorMessage("Failed to load integrations");
    }
  }, []);

  const loadCredentials = useCallback(async (): Promise<void> => {
    // 1. Apply cached value immediately if available
    try {
      const cached = await getCache<IntegrationCredentialItem[]>(CacheKey.listIntegrationCredentials);
      if (cached?.value) {
        setCredentials(cached.value);
      }
    } catch {
      // Cache read failure is non-fatal — fall through to network fetch
    }

    // 2. Fetch fresh and refresh state + cache
    try {
      const response = await API.graphql(graphqlOperation(listIntegrationCredentials));
      const responseData = getGraphData(response, "listIntegrationCredentials");
      const freshCredentials: IntegrationCredentialItem[] = responseData?.items || [];
      setCredentials(freshCredentials);
      setCache(CacheKey.listIntegrationCredentials, freshCredentials);
    } catch (loadError) {
      console.error("Failed to load credentials:", loadError);
    }
  }, []);

  // ---- componentDidMount equivalent ----
  useEffect(() => {
    document.getElementById("root")!.className = "root";
    loadAllIntegrations();
    if (isAdmin) {
      loadCredentials();
    }
  }, [isAdmin, loadAllIntegrations, loadCredentials]);

  // ----------------------------------------
  //   Credential Actions
  // ----------------------------------------

  const openCreateCredential = useCallback((preselectType?: string): void => {
    setCredentialView("create");
    setEditingCredential(null);
    setCredentialForm(
      preselectType
        ? { ...INITIAL_CREDENTIAL_FORM, type: preselectType }
        : INITIAL_CREDENTIAL_FORM,
    );
  }, []);

  const openEditCredential = useCallback((credential: IntegrationCredentialItem): void => {
    setCredentialView("edit");
    setEditingCredential(credential);
    setCredentialForm({
      type: credential.integrationType,
      displayName: credential.displayName,
      token: EMPTY_STRING, // Never pre-populate token
      showToken: false,
      errors: {},
    });
  }, []);

  const cancelCredentialForm = useCallback((): void => {
    setCredentialView("none");
    setEditingCredential(null);
    setCredentialForm(INITIAL_CREDENTIAL_FORM);
  }, []);

  const validateCredentialForm = useCallback((): Record<string, string> => {
    const errors: Record<string, string> = {};
    const isEditing = !!editingCredential;

    if (!isEditing && !credentialForm.type) {
      errors.credentialFormType = "Integration type is required";
    }
    if (!credentialForm.displayName.trim()) {
      errors.credentialFormDisplayName = "Name is required";
    }
    if (!isEditing && !credentialForm.token.trim()) {
      errors.credentialFormToken = "Token is required";
    }

    return errors;
  }, [editingCredential, credentialForm.type, credentialForm.displayName, credentialForm.token]);

  const handleSaveCredential = useCallback(async (): Promise<void> => {
    const errors = validateCredentialForm();
    if (Object.keys(errors).length > 0) {
      setCredentialForm((prev) => ({ ...prev, errors }));
      return;
    }

    try {
      if (editingCredential) {
        // Update existing credential
        const updateInput: Record<string, any> = {
          id: editingCredential.id,
          displayName: credentialForm.displayName.trim(),
        };
        if (credentialForm.token.trim()) {
          updateInput.credential = credentialForm.token.trim();
        }
        const updateResponse = await API.graphql(
          graphqlOperation(updateIntegrationCredential, { input: updateInput })
        ) as any;
        const updatedCredential = getGraphData(updateResponse, "updateIntegrationCredential");
        if (!updatedCredential?.id) {
          const backendMessage = updatedCredential?.message || "Failed to update credential — please try again.";
          setCredentialForm((prev) => ({ ...prev, errors: { credentialFormToken: backendMessage } }));
          return;
        }
      } else {
        // Create new credential
        const createResponse = await API.graphql(graphqlOperation(createIntegrationCredential, {
          input: {
            integrationType: credentialForm.type,
            displayName: credentialForm.displayName.trim(),
            credential: credentialForm.token.trim(),
          },
        })) as any;
        const createdCredential = getGraphData(createResponse, "createIntegrationCredential");
        if (!createdCredential?.id) {
          const backendMessage = createdCredential?.message || "Failed to save credential — please try again.";
          setCredentialForm((prev) => ({ ...prev, errors: { credentialFormToken: backendMessage } }));
          return;
        }
      }
      cancelCredentialForm();
      await loadCredentials();
    } catch (saveError) {
      console.error("Failed to save credential:", saveError);
      setCredentialForm((prev) => ({ ...prev, errors: { credentialFormToken: "Failed to save credential. Check the token and try again." } }));
    }
  }, [editingCredential, credentialForm, validateCredentialForm, cancelCredentialForm, loadCredentials]);

  const handleDeleteCredential = useCallback((credentialId: string): void => {
    setPendingConfirmation({ kind: "credential", id: credentialId });
  }, []);

  const executeDeleteCredential = useCallback(async (credentialId: string): Promise<void> => {
    try {
      const deleteResponse = await API.graphql(
        graphqlOperation(deleteIntegrationCredential, { input: { id: credentialId } })
      );
      const deleteResult = getGraphData(deleteResponse, "deleteIntegrationCredential");
      if (deleteResult === false) {
        setErrorMessage("Credential is still referenced by one or more integrations. Remove those integrations first.");
        return;
      }
      await loadCredentials();
    } catch (deleteError: any) {
      const serverMessage = deleteError?.errors?.[0]?.message || "";
      setErrorMessage(serverMessage || "Failed to delete credential");
    }
  }, [loadCredentials]);

  const executeDeleteIntegration = useCallback(async (integrationId: string): Promise<void> => {
    try {
      setLoading(true);
      await API.graphql(graphqlOperation(deleteIntegration, { input: { id: integrationId } }));
      await loadAllIntegrations();
      setLoading(false);
    } catch (deleteError) {
      console.error("Failed to delete integration:", deleteError);
      setLoading(false);
      setErrorMessage("Failed to delete integration");
    }
  }, [loadAllIntegrations]);

  // Centralised handler invoked when the ConfirmModal's Confirm button is clicked.
  const handleConfirmDelete = useCallback((): void => {
    if (!pendingConfirmation) return;
    setPendingConfirmation(null);
    if (pendingConfirmation.kind === "credential") {
      executeDeleteCredential(pendingConfirmation.id);
    } else if (pendingConfirmation.kind === "integration") {
      executeDeleteIntegration(pendingConfirmation.id);
    }
  }, [pendingConfirmation, executeDeleteCredential, executeDeleteIntegration]);

  const handleCancelConfirm = useCallback((): void => {
    setPendingConfirmation(null);
  }, []);

  // ----------------------------------------
  //   Modal Actions
  // ----------------------------------------

  const resetForm = useCallback((): void => {
    setSelectedType(EMPTY_STRING);
    setEditingIntegration(null);
    setTestResult(null);
    setBindingForm(INITIAL_BINDING_FORM);
  }, []);

  const openManageModal = useCallback((webapp: WebApplicationClass): void => {
    setManagingWebApp(webapp);
    setModalView("list");
    setSuccessMessage(EMPTY_STRING);
    setErrorMessage(EMPTY_STRING);
    resetForm();
  }, [resetForm]);

  const closeModal = useCallback((): void => {
    setManagingWebApp(null);
    resetForm();
  }, [resetForm]);

  // Type picker click handler. For integration types that require a saved
  // credential, block entry into the form when no matching credential exists.
  const handleTypePicked = useCallback((integType: string, defaultDisplayName: string): void => {
    if (INTEGRATION_TYPES_REQUIRING_CREDENTIAL.has(integType)) {
      const hasCredentialForType = credentials.some(
        (cred) => cred.integrationType === integType
      );
      if (!hasCredentialForType) {
        setMissingCredentialType(integType);
        return;
      }
    }
    setSelectedType(integType);
    setBindingForm((prev) => ({ ...prev, displayName: defaultDisplayName }));
  }, [credentials]);

  const dismissMissingCredentialModal = useCallback((): void => {
    setMissingCredentialType(null);
  }, []);

  // Anchor for the "Go to Credentials" jump from the missing-credential modal,
  // so the user isn't told to "scroll up and find it" manually.
  const credentialsSectionRef = useRef<HTMLDivElement | null>(null);

  // Close the missing-credential prompt, open the Add-Credential form with the
  // needed integration type pre-selected, and scroll it into view.
  const goToCredentials = useCallback((): void => {
    const preselectType = missingCredentialType;
    setMissingCredentialType(null);
    openCreateCredential(preselectType ?? undefined);
    // Wait a frame so the form has mounted before scrolling to it.
    requestAnimationFrame(() => {
      credentialsSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }, [missingCredentialType, openCreateCredential]);

  // ----------------------------------------
  //   Binding CRUD
  // ----------------------------------------

  const buildConnectionConfig = useCallback((): Record<string, string> => {
    const config: Record<string, string> = {};
    if (bindingForm.baseUrl) config.baseUrl = bindingForm.baseUrl;
    if (bindingForm.projectKey) config.projectKey = bindingForm.projectKey;
    if (bindingForm.issueType) config.issueType = bindingForm.issueType;
    if (bindingForm.authMode) config.authMode = bindingForm.authMode;
    if (bindingForm.email) config.email = bindingForm.email;
    if (bindingForm.format) config.format = bindingForm.format;
    if (bindingForm.workspaceId) config.workspaceId = bindingForm.workspaceId;
    return config;
  }, [bindingForm]);

  const buildEventFilter = useCallback((): Record<string, any> => {
    return {
      eventTypes: bindingForm.eventTypes,
      minSeverity: bindingForm.minSeverity || undefined,
    };
  }, [bindingForm.eventTypes, bindingForm.minSeverity]);

  const validateBindingForm = useCallback((): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!bindingForm.displayName.trim()) {
      errors.formDisplayName = "Name is required";
    }

    if (INTEGRATION_TYPES_REQUIRING_CREDENTIAL.has(selectedType) && !bindingForm.credentialId) {
      errors.formCredentialId = "A saved credential is required";
    }

    if (selectedType !== "sentinel") {
      const urlValue = bindingForm.baseUrl.trim();
      if (!urlValue) {
        errors.formBaseUrl = "URL is required";
      } else if (!urlValue.startsWith("https://")) {
        errors.formBaseUrl = "URL must start with https://";
      }
    }

    if (selectedType === "jira") {
      if (bindingForm.authMode === "cloud" && !bindingForm.email.trim()) {
        errors.formEmail = "Email is required for Cloud authentication";
      }
      if (!bindingForm.projectKey.trim()) {
        errors.formProjectKey = "Project key is required";
      }
    }

    if (selectedType === "sentinel" && !bindingForm.workspaceId.trim()) {
      errors.formWorkspaceId = "Workspace ID is required";
    }

    return errors;
  }, [bindingForm, selectedType]);

  const handleCreate = useCallback(async (): Promise<void> => {
    const errors = validateBindingForm();
    if (Object.keys(errors).length > 0) {
      setBindingForm((prev) => ({ ...prev, errors }));
      return;
    }

    if (!managingWebApp) return;

    const input: Record<string, any> = {
      webAppId: managingWebApp.id,
      integrationType: selectedType,
      displayName: bindingForm.displayName,
      connectionConfig: JSON.stringify(buildConnectionConfig()),
      eventFilter: JSON.stringify(buildEventFilter()),
    };
    if (INTEGRATION_TYPES_REQUIRING_CREDENTIAL.has(selectedType)) {
      input.credentialId = bindingForm.credentialId;
    }

    try {
      setLoading(true);
      setErrorMessage(EMPTY_STRING);
      await API.graphql(graphqlOperation(createIntegration, { input }));
      setModalView("list");
      setSuccessMessage("Integration created");
      setLoading(false);
      resetForm();
      await loadAllIntegrations();
    } catch (createError) {
      console.error("Failed to create integration:", createError);
      setLoading(false);
      setErrorMessage("Failed to create integration");
    }
  }, [validateBindingForm, managingWebApp, selectedType, bindingForm, buildConnectionConfig, buildEventFilter, resetForm, loadAllIntegrations]);

  const handleUpdate = useCallback(async (): Promise<void> => {
    const errors = validateBindingForm();
    if (Object.keys(errors).length > 0) {
      setBindingForm((prev) => ({ ...prev, errors }));
      return;
    }

    if (!editingIntegration) return;

    const input: Record<string, any> = {
      id: editingIntegration.id,
      displayName: bindingForm.displayName,
      connectionConfig: JSON.stringify(buildConnectionConfig()),
      eventFilter: JSON.stringify(buildEventFilter()),
    };
    if (INTEGRATION_TYPES_REQUIRING_CREDENTIAL.has(selectedType) && bindingForm.credentialId) {
      input.credentialId = bindingForm.credentialId;
    }

    try {
      setLoading(true);
      setErrorMessage(EMPTY_STRING);
      await API.graphql(graphqlOperation(updateIntegration, { input }));
      setModalView("list");
      setSuccessMessage("Integration updated");
      setLoading(false);
      resetForm();
      await loadAllIntegrations();
    } catch (updateError) {
      console.error("Failed to update integration:", updateError);
      setLoading(false);
      setErrorMessage("Failed to update integration");
    }
  }, [validateBindingForm, editingIntegration, selectedType, bindingForm, buildConnectionConfig, buildEventFilter, resetForm, loadAllIntegrations]);

  const handleDelete = useCallback((integrationId: string, displayName: string): void => {
    setPendingConfirmation({ kind: "integration", id: integrationId, displayName });
  }, []);

  const handleToggle = useCallback(async (integrationId: string, enabled: boolean): Promise<void> => {
    try {
      await API.graphql(graphqlOperation(toggleIntegration, { input: { id: integrationId, enabled } }));
      await loadAllIntegrations();
    } catch (toggleError) {
      console.error("Failed to toggle integration:", toggleError);
    }
  }, [loadAllIntegrations]);

  const handleTestConnection = useCallback(async (): Promise<void> => {
    try {
      setTestingConnection(true);
      setTestResult(null);

      const testInput: Record<string, any> = {
        id: editingIntegration?.id || undefined,
        integrationType: selectedType || editingIntegration?.integrationType,
        connectionConfig: JSON.stringify(buildConnectionConfig()),
      };

      // Prefer the form-selected credentialId; fall back to the existing integration's credential
      const resolvedCredentialId =
        bindingForm.credentialId ||
        editingIntegration?.credentialId ||
        undefined;
      if (resolvedCredentialId) {
        testInput.credentialId = resolvedCredentialId;
      }

      const response = await API.graphql(
        graphqlOperation(testIntegrationConnection, { input: testInput })
      );
      const result = getGraphData(response, "testIntegrationConnection");
      setTestingConnection(false);
      setTestResult(result);
    } catch (testError) {
      console.error("Connection test failed:", testError);
      setTestingConnection(false);
      setTestResult({ success: false, message: "Connection test failed" });
    }
  }, [editingIntegration, selectedType, buildConnectionConfig, bindingForm.credentialId]);

  const populateFormFromIntegration = useCallback((integration: IntegrationItem): void => {
    const connectionConfig = JSON.parse(integration.connectionConfig || "{}");
    const eventFilter = JSON.parse(integration.eventFilter || "{}");
    setEditingIntegration(integration);
    setSelectedType(integration.integrationType);
    setModalView("edit");
    setTestResult(null);
    setBindingForm({
      errors: {},
      displayName: integration.displayName,
      credentialId: integration.credentialId || EMPTY_STRING,
      baseUrl: connectionConfig.baseUrl || EMPTY_STRING,
      projectKey: connectionConfig.projectKey || EMPTY_STRING,
      issueType: connectionConfig.issueType || "Bug",
      authMode: connectionConfig.authMode || "pat",
      email: connectionConfig.email || EMPTY_STRING,
      eventTypes: eventFilter.eventTypes || ["*"],
      minSeverity: eventFilter.minSeverity || EMPTY_STRING,
      format: connectionConfig.format || "json",
      workspaceId: connectionConfig.workspaceId || EMPTY_STRING,
    });
  }, []);

  // ----------------------------------------
  //   Render: Credentials Section
  // ----------------------------------------

  const renderCredentialsSection = (): React.ReactElement => {
    return (
      <Card>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Credentials</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Saved API tokens and keys — the reusable secret only, not tied to any one
              application. You can store multiple per integration type (for example,
              separate Jira tokens for production and staging). The connection URL and
              app-specific settings (such as a Jira base URL and project key) are set per
              application below, under Applications → Manage.
            </p>
          </div>
          {credentialView === "none" && (
            // Matches the Test button on the WebApplication page and
            // the Add User button on the Users page — outline pill,
            // blue border + blue text on white. Crisper than the prior
            // solid blue fill; reads as on-brand without dominating.
            <button
              type="button"
              onClick={() => openCreateCredential()}
              className="inline-flex items-center justify-center gap-1.5 rounded-full border border-blue-500 px-4 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 hover:border-blue-600 transition-colors duration-100"
            >
              <Plus className="h-3.5 w-3.5" />
              Add Credential
            </button>
          )}
        </div>

        {credentialView !== "none" && renderCredentialForm()}

        <CardContent className="p-0">
          {credentials.length === 0 && credentialView === "none" && (
            <div className="px-6 py-12 text-center">
              <Key className="mx-auto h-10 w-10 text-gray-300" />
              <p className="mt-3 text-sm font-medium text-gray-700">
                No credentials saved yet
              </p>
              <p className="mt-1 text-sm text-gray-500">
                Add a token or API key to link integrations like Jira.
              </p>
            </div>
          )}
          {credentials.length > 0 && (
            <div className="flex flex-col gap-4 p-4">
              {credentials.map((credential) => renderCredentialRow(credential))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderCredentialRow = (credential: IntegrationCredentialItem): React.ReactElement => {
    const IconComponent = INTEGRATION_ICON_MAP[credential.integrationType];
    const typeMeta = INTEGRATION_TYPE_META[credential.integrationType];
    const isExpired =
      !!credential.credentialExpiresAt &&
      credential.credentialExpiresAt > 0 &&
      credential.credentialExpiresAt < Date.now() / 1000;

    return (
      <div
        key={credential.id}
        className="flex items-start gap-5 px-6 py-5 rounded-xl bg-white border border-gray-200 shadow-sm transition-shadow hover:shadow-md"
      >
        <div className="text-gray-400 shrink-0 mt-0.5">
          {IconComponent && <IconComponent size={22} />}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-base font-semibold text-gray-900 truncate">{credential.displayName}</p>
          <p className="mt-1 text-sm text-gray-500 truncate">
            {typeMeta?.displayName || credential.integrationType}
            {credential.maskedSuffix && (
              <span className="ml-1.5 font-mono tracking-widest text-gray-500">
                ····{credential.maskedSuffix}
              </span>
            )}
            {isExpired && (
              <span className="ml-1.5 text-red-500 font-medium not-italic">Expired</span>
            )}
            {!isExpired && credential.credentialExpiresAt && credential.credentialExpiresAt > 0 && (
              <span className="ml-1.5">
                · Expires {formatDate(credential.credentialExpiresAt)}
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0 self-center">
          <button
            type="button"
            onClick={() => openEditCredential(credential)}
            className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={() => handleDeleteCredential(credential.id)}
            className="inline-flex items-center justify-center rounded-md border border-red-200 bg-white px-2.5 py-1 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 cursor-pointer"
          >
            Delete
          </button>
        </div>
      </div>
    );
  };

  const renderCredentialForm = (): React.ReactElement => {
    const isEditing = !!editingCredential;
    const errors = credentialForm.errors;

    return (
      <div className="px-6 py-5 border-b border-gray-100 bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-800 mb-4">
          {isEditing ? "Edit Credential" : "Add Credential"}
        </h3>

        <div className="flex flex-col gap-3 max-w-md">
          {/* Integration type — only when creating */}
          {!isEditing && (
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-2">
                Integration type <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-4 gap-2">
                {Object.keys(INTEGRATION_TYPE_META).map((integType) => {
                  const meta = INTEGRATION_TYPE_META[integType];
                  const IconComponent = INTEGRATION_ICON_MAP[integType];
                  const isSelected = credentialForm.type === integType;
                  return (
                    <button
                      key={integType}
                      type="button"
                      onClick={() => setCredentialForm((prev) => ({ ...prev, type: integType }))}
                      className={[
                        "flex flex-col items-center gap-1.5 rounded-lg border px-2 py-3 text-center transition-colors cursor-pointer",
                        isSelected
                          ? "border-blue-500 bg-blue-50 text-blue-600"
                          : "border-gray-200 bg-gray-50 text-gray-500 hover:border-blue-300 hover:bg-blue-50",
                      ].join(" ")}
                    >
                      {IconComponent && <IconComponent size={22} />}
                      <span className="text-xs font-medium leading-tight">{meta.displayName}</span>
                    </button>
                  );
                })}
              </div>
              {errors.credentialFormType && (
                <p className="text-xs text-red-500 mt-1">{errors.credentialFormType}</p>
              )}
            </div>
          )}

          {/* Display name */}
          <FormField
            label="Name"
            required
            error={errors.credentialFormDisplayName}
          >
            <Input
              type="text"
              placeholder="e.g. Production Jira Token"
              value={credentialForm.displayName}
              onChange={(nameEvent) => setCredentialForm((prev) => ({ ...prev, displayName: nameEvent.target.value }))}
              hasError={!!errors.credentialFormDisplayName}
            />
          </FormField>

          {/* Token */}
          <FormField
            label={isEditing ? "New token (leave blank to keep existing)" : "Token"}
            required={!isEditing}
            error={errors.credentialFormToken}
          >
            <Input
              type={credentialForm.showToken ? "text" : "password"}
              placeholder={isEditing ? "••••••••" : "Enter token or API key"}
              value={credentialForm.token}
              onChange={(tokenEvent) => setCredentialForm((prev) => ({ ...prev, token: tokenEvent.target.value }))}
              hasError={!!errors.credentialFormToken}
              rightSlot={
                <button
                  type="button"
                  onClick={() => setCredentialForm((prev) => ({ ...prev, showToken: !prev.showToken }))}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                  tabIndex={-1}
                  aria-label={credentialForm.showToken ? "Hide token" : "Show token"}
                >
                  {credentialForm.showToken ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              }
            />
          </FormField>

          {/* Actions */}
          <div className="flex items-center gap-2 mt-1">
            <button
              type="button"
              onClick={() => handleSaveCredential()}
              className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 cursor-pointer"
            >
              {isEditing ? "Save Changes" : "Add Credential"}
            </button>
            <button
              type="button"
              onClick={() => cancelCredentialForm()}
              className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ----------------------------------------
  //   Render: App List
  // ----------------------------------------

  const renderAppRow = (webapp: WebApplicationClass): React.ReactElement => {
    const appIntegrations = integrationsByApp[webapp.id] || [];
    const appColor  = webapp.color || deriveAppColor(webapp.name);
    const appAbbrev = webapp.abbrev || deriveAppAbbrev(webapp.name);

    return (
      <div
        key={webapp.id}
        className="flex items-start gap-5 px-6 py-5 rounded-xl bg-white border border-gray-200 shadow-sm transition-shadow hover:shadow-md"
      >
        {/* App badge — same colored avatar used on the Applications page,
            so an app reads identically across Dashboard / Applications /
            Integrations. */}
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-sm font-bold text-white mt-0.5"
          style={{ backgroundColor: appColor }}
        >
          {appAbbrev}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-base font-semibold text-gray-900 truncate">{webapp.name}</p>
          {webapp.url && (
            <p className="mt-1 text-sm text-gray-500 truncate">{webapp.url}</p>
          )}
          {/* Integration badges — min-h reserves height so rows stay
              aligned whether the app has integrations or not. */}
          <div className="mt-3 flex min-h-[22px] items-center gap-2 flex-wrap">
            {loading ? (
              <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-200 border-t-gray-400" />
            ) : appIntegrations.length === 0 ? (
              <span className="text-xs text-gray-400">None configured</span>
            ) : (
              appIntegrations.map((integration) => {
                const status = getIntegrationStatus(integration);
                const IconComponent = INTEGRATION_ICON_MAP[integration.integrationType];
                const typeMeta = INTEGRATION_TYPE_META[integration.integrationType];
                return (
                  <span
                    key={integration.id}
                    title={`${typeMeta?.displayName || integration.integrationType}: ${status.label}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700"
                  >
                    {IconComponent && <IconComponent size={12} />}
                    {typeMeta?.displayName || integration.integrationType}
                    <span
                      className="h-1.5 w-1.5 rounded-full shrink-0"
                      style={{ backgroundColor: status.color }}
                    />
                  </span>
                );
              })
            )}
          </div>
        </div>

        {isAdmin && (
          <button
            type="button"
            onClick={() => openManageModal(webapp)}
            className="self-center inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer shrink-0"
          >
            Manage
          </button>
        )}
      </div>
    );
  };

  // ----------------------------------------
  //   Render: Modal
  // ----------------------------------------

  const renderModal = (): React.ReactElement | null => {
    if (!managingWebApp) return null;

    return ReactDOM.createPortal(
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        onClick={() => closeModal()}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div
          className="relative w-full max-w-xl rounded-xl bg-white shadow-xl flex flex-col max-h-[90vh]"
          onClick={(clickEvent) => clickEvent.stopPropagation()}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-gray-900">Integrations</h2>
              <p className="text-xs text-gray-400 truncate mt-0.5">{managingWebApp.name}</p>
            </div>
            <button
              type="button"
              onClick={() => closeModal()}
              className="rounded-md p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer ml-4 shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="overflow-y-auto flex-1 px-6 py-5">
            {successMessage && (
              <p className="text-sm text-emerald-600 mb-4">{successMessage}</p>
            )}
            {errorMessage && (
              <p className="text-sm text-red-500 mb-4">{errorMessage}</p>
            )}
            {modalView === "list" && renderModalList()}
            {modalView === "add" && !selectedType && renderTypePicker()}
            {(modalView === "add" || modalView === "edit") && selectedType && renderForm()}
          </div>
        </div>
      </div>,
      document.body
    );
  };

  const renderModalList = (): React.ReactElement => {
    if (!managingWebApp) return <></>;

    const appIntegrations = integrationsByApp[managingWebApp.id] || [];

    return (
      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={() => {
            setModalView("add");
            setSelectedType(EMPTY_STRING);
            setSuccessMessage(EMPTY_STRING);
          }}
          className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 cursor-pointer self-start"
        >
          + Add Integration
        </button>

        {appIntegrations.length === 0 && (
          <p className="text-sm text-gray-400 py-4">No integrations configured for this app yet.</p>
        )}

        {appIntegrations.map((integration) => {
          const status = getIntegrationStatus(integration);
          const IconComponent = INTEGRATION_ICON_MAP[integration.integrationType];
          const typeMeta = INTEGRATION_TYPE_META[integration.integrationType];

          return (
            <div
              key={integration.id}
              className="flex items-center gap-3 rounded-xl bg-white border border-gray-200 shadow-sm px-4 py-3"
            >
              <div className="shrink-0 text-gray-500">
                {IconComponent && <IconComponent size={22} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-800">
                    {typeMeta?.displayName || integration.integrationType}
                  </span>
                  <span className="text-xs text-gray-400">—</span>
                  <span className="text-xs text-gray-600 truncate">{integration.displayName}</span>
                  <span
                    className="h-2 w-2 rounded-full shrink-0"
                    style={{ backgroundColor: status.color }}
                    title={status.label}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-0.5">{status.label}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => populateFormFromIntegration(integration)}
                  className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => handleToggle(integration.id, !integration.enabled)}
                  className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer"
                >
                  {integration.enabled ? "Disable" : "Enable"}
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(integration.id, integration.displayName)}
                  className="inline-flex items-center justify-center rounded-md border border-red-200 bg-white px-2.5 py-1 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 cursor-pointer"
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderTypePicker = (): React.ReactElement => {
    return (
      <div>
        <p className="text-sm text-gray-600 mb-4">Choose an integration type to configure.</p>
        <div className="grid grid-cols-3 gap-3">
          {Object.keys(INTEGRATION_TYPE_META).map((integType) => {
            const meta = INTEGRATION_TYPE_META[integType];
            const IconComponent = INTEGRATION_ICON_MAP[integType];
            return (
              <button
                key={integType}
                type="button"
                onClick={() => handleTypePicked(integType, meta.displayName)}
                className="flex flex-col items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-4 text-center transition-colors hover:border-blue-300 hover:bg-blue-50 cursor-pointer"
              >
                <span className="text-gray-500">
                  {IconComponent && <IconComponent size={28} />}
                </span>
                <span className="text-sm font-medium text-gray-800">{meta.displayName}</span>
                <span className="text-xs text-gray-400">{meta.description}</span>
              </button>
            );
          })}
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={() => setModalView("list")}
            className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  const renderForm = (): React.ReactElement => {
    const isEditing = modalView === "edit";
    const type = selectedType;
    const formErrors = bindingForm.errors;

    // Credentials filtered to the current integration type (for the credential picker)
    const credentialsForType = credentials.filter((cred) => cred.integrationType === type);
    const requiresCredential = INTEGRATION_TYPES_REQUIRING_CREDENTIAL.has(type);

    return (
      <div className="flex flex-col gap-4">
        <h3 className="text-sm font-semibold text-gray-800">
          {isEditing ? "Edit" : "Configure"} {INTEGRATION_TYPE_META[type]?.displayName || type}
        </h3>

        {/* Display Name */}
        <FormField label="Name" required error={formErrors.formDisplayName}>
          <Input
            type="text"
            placeholder="e.g. Portal Bug Board"
            value={bindingForm.displayName}
            onChange={(nameEvent) => setBindingForm((prev) => ({ ...prev, displayName: nameEvent.target.value }))}
            hasError={!!formErrors.formDisplayName}
          />
        </FormField>

        {/* Credential picker — Jira, Splunk, Sentinel only */}
        {requiresCredential && (
          <FormField
            label={CREDENTIAL_TOKEN_LABEL[type] || "Credential"}
            required
            error={formErrors.formCredentialId}
          >
            {credentialsForType.length === 0 ? (
              <p className="text-xs text-orange-500 py-1">
                No {INTEGRATION_TYPE_META[type]?.displayName} credentials found. Add one in the
                Credentials section above, then return here.
              </p>
            ) : (
              <SortSelect
                value={bindingForm.credentialId}
                onChange={(nextId) => setBindingForm((prev) => ({ ...prev, credentialId: nextId }))}
                options={credentialsForType.map((cred) => ({
                  value: cred.id,
                  label: `${cred.displayName}${cred.maskedSuffix ? ` (···${cred.maskedSuffix})` : ""}`,
                }))}
                ariaLabel="Select credential…"
                icon={Key}
                hasError={!!formErrors.formCredentialId}
              />
            )}
          </FormField>
        )}

        {/* URL — not shown for Sentinel (workspace ID) */}
        {type !== "sentinel" && (
          <FormField
            label={
              type === "slack" || type === "teams"
                ? "Webhook URL"
                : type === "splunk"
                ? "HEC URL"
                : "Base URL"
            }
            required
            error={formErrors.formBaseUrl}
          >
            <Input
              type="url"
              placeholder="https://…"
              value={bindingForm.baseUrl}
              onChange={(urlEvent) => setBindingForm((prev) => ({ ...prev, baseUrl: urlEvent.target.value }))}
              hasError={!!formErrors.formBaseUrl}
            />
          </FormField>
        )}

        {/* Jira-specific fields */}
        {type === "jira" && (
          <>
            <FormField label="Auth Mode">
              <SortSelect
                value={bindingForm.authMode}
                onChange={(nextMode) => setBindingForm((prev) => ({ ...prev, authMode: nextMode }))}
                options={[
                  { value: "pat",   label: "Personal Access Token (Data Center)" },
                  { value: "cloud", label: "API Token (Cloud)" },
                ]}
                ariaLabel="Auth Mode"
                icon={Lock}
              />
            </FormField>
            {bindingForm.authMode === "cloud" && (
              <FormField label="Email" required error={formErrors.formEmail}>
                <Input
                  type="email"
                  placeholder="you@company.com"
                  value={bindingForm.email}
                  onChange={(emailEvent) => setBindingForm((prev) => ({ ...prev, email: emailEvent.target.value }))}
                  hasError={!!formErrors.formEmail}
                />
              </FormField>
            )}
            <FormField label="Project Key" required error={formErrors.formProjectKey}>
              <Input
                type="text"
                placeholder="e.g. PORTAL"
                value={bindingForm.projectKey}
                onChange={(projectKeyEvent) =>
                  setBindingForm((prev) => ({ ...prev, projectKey: projectKeyEvent.target.value }))
                }
                hasError={!!formErrors.formProjectKey}
              />
            </FormField>
            <FormField label="Issue Type">
              <Input
                type="text"
                placeholder="e.g. Bug, Task, Story"
                value={bindingForm.issueType}
                onChange={(issueEvent) => setBindingForm((prev) => ({ ...prev, issueType: issueEvent.target.value }))}
              />
            </FormField>
          </>
        )}

        {/* Splunk output format */}
        {type === "splunk" && (
          <FormField label="Output Format">
            <SortSelect
              value={bindingForm.format}
              onChange={(nextFormat) => setBindingForm((prev) => ({ ...prev, format: nextFormat }))}
              options={[
                { value: "json", label: "NIMIS JSON" },
                { value: "ocsf", label: "OCSF (Open Cybersecurity Schema)" },
              ]}
              ariaLabel="Output Format"
              icon={FileText}
            />
          </FormField>
        )}

        {/* Sentinel workspace ID */}
        {type === "sentinel" && (
          <FormField label="Workspace ID" required error={formErrors.formWorkspaceId}>
            <Input
              type="text"
              placeholder="Log Analytics Workspace ID (UUID)"
              value={bindingForm.workspaceId}
              onChange={(wsEvent) => setBindingForm((prev) => ({ ...prev, workspaceId: wsEvent.target.value }))}
              hasError={!!formErrors.formWorkspaceId}
            />
          </FormField>
        )}

        {/* Minimum severity filter. Ordering and labels match the
            risk-rating taxonomy in models/enums.py — Critical is the
            top severity, "High and above" includes Critical, etc.
            BE-side, the Critical rank is added by
            IntegrationsCriticalSeverity_BEBrief.md; without that brief
            applied, every non-empty threshold silently drops Critical
            findings. */}
        <FormField label="Minimum Severity">
          <SortSelect
            value={bindingForm.minSeverity}
            onChange={(nextSeverity) => setBindingForm((prev) => ({ ...prev, minSeverity: nextSeverity }))}
            options={[
              { value: "",         label: "All severities"      },
              { value: "Info",     label: "Info and above"      },
              { value: "Low",      label: "Low and above"       },
              { value: "Medium",   label: "Medium and above"    },
              { value: "High",     label: "High and above"      },
              { value: "Critical", label: "Critical only"       },
            ]}
            ariaLabel="Minimum Severity"
            icon={ShieldAlert}
          />
        </FormField>

        {/* Test connection */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={testingConnection}
            onClick={() => handleTestConnection()}
            className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer disabled:opacity-50 disabled:cursor-default"
          >
            {testingConnection ? "Testing…" : "Test Connection"}
          </button>
          {testResult && (
            <span className={`text-xs ${testResult.success ? "text-emerald-600" : "text-red-500"}`}>
              {testResult.success ? "Connected" : testResult.message}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-100 mt-2">
          <button
            type="button"
            onClick={() => {
              resetForm();
              setModalView("list");
            }}
            className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => (isEditing ? handleUpdate() : handleCreate())}
            className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 cursor-pointer"
          >
            {isEditing ? "Save Changes" : "Create Integration"}
          </button>
        </div>
      </div>
    );
  };

  // Info-only modal shown when the user picks an integration type that
  // requires a saved credential and none exists yet.
  const renderMissingCredentialModal = (): React.ReactElement | null => {
    const integType = missingCredentialType;
    if (!integType) return null;

    const displayName = INTEGRATION_TYPE_META[integType]?.displayName || integType;
    return (
      <ConfirmModal
        show
        title={`No ${displayName} credentials found`}
        message={
          <>
            You need to add a {displayName} credential before you can configure this
            integration. A credential is the reusable {displayName} token; you'll set the
            connection URL and app-specific settings here afterwards.
          </>
        }
        confirmLabel="Go to Credentials"
        cancelLabel="Not now"
        onConfirm={goToCredentials}
        onCancel={dismissMissingCredentialModal}
      />
    );
  };

  // Render the confirmation modal for destructive actions (delete credential,
  // delete integration). Title and message are derived from pendingConfirmation.
  const renderConfirmModal = (): React.ReactElement | null => {
    const pending = pendingConfirmation;
    if (!pending) return null;

    let title: string;
    let message: React.ReactNode;
    if (pending.kind === "credential") {
      title = "Delete this credential?";
      message = "Any integrations using it will stop working.";
    } else {
      title = "Delete this integration?";
      message = (
        <>
          <span className="font-medium text-gray-900">{pending.displayName}</span> will stop receiving events. This action cannot be undone.
        </>
      );
    }

    return (
      <ConfirmModal
        show
        title={title}
        message={message}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        destructive
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelConfirm}
      />
    );
  };

  // ----------------------------------------
  //   Main Render
  // ----------------------------------------

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gray-50">
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8 flex flex-col gap-6">
      {/* Header — h1 + descriptive subtitle on the left, My Apps /
          All Apps toggle on the right. Toggle is admin/owner only and
          mirrors the affordance on the Dashboard and Applications
          page; the choice is persisted per-user in localStorage so it
          carries between the three surfaces. */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Integrations</h1>
          <p className="text-sm text-gray-500 mt-1">
            Configure Slack, Jira, and other integrations per application.
            {!isAdmin && " Contact an admin to make changes."}
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center rounded-lg border border-gray-200 bg-gray-100 p-0.5 gap-0.5">
            {[
              { label: "My Apps", value: true },
              { label: "All Apps", value: false },
            ].map(({ label, value }) => (
              <button
                key={label}
                type="button"
                onClick={() => setDisplayMyApps(value)}
                className={[
                  "rounded-md px-3 py-1.5 text-sm font-medium transition-all",
                  displayMyApps === value
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700",
                ].join(" ")}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Error outside modal */}
      {errorMessage && !managingWebApp && (
        <p className="text-sm text-red-500">{errorMessage}</p>
      )}

      {/* Credentials section — admin only */}
      {isAdmin && (
        <div ref={credentialsSectionRef}>{renderCredentialsSection()}</div>
      )}

      {/* Web apps + integration badges */}
      <Card>
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Applications</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Integrations configured per application
          </p>
        </div>

        <CardContent className="p-0">
          {/* Empty-state copy distinguishes "no apps in the org" from
              "no apps match the current filter" — the latter happens
              when an admin toggles to My Apps but owns none. Matches the
              Globe empty-state pattern used on the Dashboard and
              Applications page so the surfaces read consistently. */}
          {listOfWebapps.length === 0 ? (
            <div className="px-6 py-16 text-center">
              <Globe className="mx-auto h-10 w-10 text-gray-300" />
              <p className="mt-3 text-sm font-medium text-gray-700">
                No applications yet
              </p>
              <p className="mt-1 text-sm text-gray-500">
                Enrol an application to configure its integrations.
              </p>
            </div>
          ) : visibleWebapps.length === 0 ? (
            <div className="px-6 py-16 text-center">
              <Globe className="mx-auto h-10 w-10 text-gray-300" />
              <p className="mt-3 text-sm font-medium text-gray-700">
                No applications you own
              </p>
              <p className="mt-1 text-sm text-gray-500">
                None of your organization's applications are assigned to you yet.
              </p>
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => setDisplayMyApps(false)}
                  className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  View all applications
                </button>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-4 p-4">
              {visibleWebapps.map((webapp) => renderAppRow(webapp))}
            </div>
          )}
        </CardContent>
      </Card>

      {renderModal()}
      {renderConfirmModal()}
      {renderMissingCredentialModal()}
    </div>
    </div>
  );
}

export default Integrations;
